import { ApiResponse, axiosInstance } from '@/lib/axios';
import { FetchParams, IPagination } from '@/types/general.type';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';
import { TChannel } from '../../../_shared/types/channel.type';

export type TGetListIntegrationMetaResponse = ApiResponse<IPagination<TChannel>>;
export type TGetListIntegrationMetaParams = FetchParams;

export const getListIntegrationMeta = async (
  params: TGetListIntegrationMetaParams
): Promise<TGetListIntegrationMetaResponse> => {
  const url = '/integration/meta/org-channel';

  return axiosInstance.get(url, { params });
};

export const GET_LIST_INTEGRATION_META_QUERY_KEY = 'get-list-integration-meta';

export const getListIntegrationMetaQueryOptions = <TData = IPagination<TChannel>>(
  params: TGetListIntegrationMetaParams,
  select?: (data: TGetListIntegrationMetaResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_LIST_INTEGRATION_META_QUERY_KEY,
      {
        page: params?.page,
        limit: params?.limit,
        search: params?.search,
        sortDir: params?.sortDir,
      },
    ],
    queryFn: () => getListIntegrationMeta(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useGetListIntegrationMetaQuery<TData>(options: {
  params: TGetListIntegrationMetaParams;
  queryConfig?: QueryConfig<typeof getListIntegrationMetaQueryOptions<TData>>;
  select: (data: TGetListIntegrationMetaResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useGetListIntegrationMetaQuery(options: {
  params: TGetListIntegrationMetaParams;
  queryConfig?: QueryConfig<typeof getListIntegrationMetaQueryOptions<TGetListIntegrationMetaResponse>>;
}): ReturnType<typeof useQuery<TGetListIntegrationMetaResponse>>;

// Implementation
export function useGetListIntegrationMetaQuery<TData = TGetListIntegrationMetaResponse>({
  queryConfig,
  params,
  select,
}: {
  params: TGetListIntegrationMetaParams;
  queryConfig?: QueryConfig<typeof getListIntegrationMetaQueryOptions<TData | TGetListIntegrationMetaResponse>>;
  select?: (data: TGetListIntegrationMetaResponse) => TData;
}) {
  return useQuery({
    ...getListIntegrationMetaQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<
    typeof useQuery<TData extends TGetListIntegrationMetaResponse ? TGetListIntegrationMetaResponse : TData>
  >;
}
